/**
 * The app's own top bar, replacing the vendored `AppHeader`. 2026-08-05 revamp.
 *
 * Same reason as `AppSidebar`: the vendored header is inline-styled and under a checksum
 * manifest, so it can be neither re-skinned nor edited. See that file's docstring.
 *
 * ## 🔴 The two safety rules this component exists to keep
 *
 * `UI-Design-Spec.md#navigation` is explicit, and calls one of them out as not a preference:
 *
 * > The kill switch is present on every screen, in the same place, at all times — this is a
 * > safety requirement, not a layout preference — and it never moves when the mode pill
 * > changes width.
 *
 * Both hold here **structurally rather than by measurement**: the switch is the last child of
 * a right-hand cluster anchored to the trailing edge, and the mode pill is in the left
 * cluster. A wider pill grows into the flexible middle, so nothing on the right can move. It
 * is worth stating because the previous shell satisfied the same rule by *refusing to put
 * anything else in the header at all* — which is how the account row became an orphan strip
 * across the bottom of every screen.
 *
 * ## `CMF · CMP · DNS` was the density control, and it is now gone entirely
 *
 * It began as three unexplained three-letter codes, became three drawn row-height marks, and was
 * then removed at the operator's instruction: *"not necessary toggle table height. Remove."*
 *
 * ⚠ **That is the right call and worth recording so it is not helpfully re-added.** A per-screen
 * row-height preference is a control that earns its place on a surface where the operator reads
 * hundreds of rows and needs to trade legibility for span. This product has one reader, and the
 * two tables that matter already choose for themselves: the fleet's flat table asks for `dense`
 * because nine per-candidate measurements want the width, and the family summary refuses it
 * because seven rows carrying a 22px mark do not fit a 26px row. A global control could only
 * disagree with both.
 *
 * ⚠ **No search field.** The reference world puts a prominent one here and this build
 * deliberately does not, because `CommandPalette` is not wired to anything yet: a search box
 * that opens nothing is a dead affordance, and matching a screenshot is not a reason to ship
 * one. It is the next thing this bar should gain, not something it can fake.
 */

import type { JSX } from 'react';

import { Icon } from '../design/components/icon/Icon.jsx';
import { KillSwitch } from '../design/components/chrome/KillSwitch.jsx';
import { type Mode, ModePill } from '../design/components/chrome/ModeMarker.jsx';
import { MODE_LABELS, type TimestampMode, resolvedZone } from '../ui/timestamps';

/**
 * ` (Europe/Amsterdam)` for the toggle's title, or nothing where `Intl` cannot say.
 *
 * The zone belongs in the title rather than on the pill: `CEST` is jargon that changes twice a
 * year, and a control whose label silently became `CET` in October would read as a fault.
 */
function zoneSuffix(): string {
  const zone = resolvedZone();
  return zone ? ` (${zone})` : '';
}

/**
 * The phone's compression of `ModePill`'s text, keyed by the same mode values.
 *
 * A map rather than a `slice` of the pill's own string: the pill is vendored and its wording is
 * its own, so deriving from it would make a phone label change silently when the pill's copy did.
 *
 * 🔴 **Every entry keeps the consequence and drops the label.** `LIVE · REAL MONEY` compresses to
 * `REAL MONEY`, not `LIVE`, because taking the first word keeps the name of the mode and throws
 * away what it costs you. ⚠ `onchain` broke that rule until 2026-08-06 — it read `ON-CHAIN`, which
 * is the label, while `IRREVERSIBLE` is the whole reason C38 escalates this mode to a fourth
 * variant. Measured at 360px: `ON-CHAIN · IRREVERSIBLE` is 195px and collapses the screen title to
 * **zero**; `IRREVERSIBLE` is 119px, which is what `REAL MONEY` already costs.
 *
 * The full wording stays reachable in `title` — the compression is a width decision, not a
 * decision about what the operator is allowed to find out.
 */
export const MODE_SHORT: Readonly<Record<string, string>> = {
  live: 'REAL MONEY',
  paper: 'PAPER',
  onchain: 'IRREVERSIBLE',
};

/** What the desktop pill spells out, carried into `title` on the phone's compressed form. */
export const MODE_FULL: Readonly<Record<string, string>> = {
  live: 'LIVE · REAL MONEY',
  paper: 'PAPER · TESTNET',
  onchain: 'LIVE · ON-CHAIN · IRREVERSIBLE',
};

export function AppTopBar({
  menuOpen = false,
  onMenu,
  title,
  mode,
  timestampMode,
  onTimestampMode,
  theme,
  onTheme,
  onKill,
  marker,
  markerShort,
  markerTitle,
}: {
  /** Phone only: the drawer's open state, so the toggle can say which way it goes. */
  menuOpen?: boolean;
  onMenu?: () => void;
  title: string;
  mode: Mode;
  /**
   * A third marker, shown **instead of** the mode pill when `mode` is null.
   *
   * 🔴 **It exists because the Stocks section is neither of the two modes.** Amber says *this
   * system can act with real money*; blue says *this money is fictional*. Trading 212 holdings are
   * real money this system cannot touch, which is a third thing, and adding a third *colour* would
   * be the one change the two-mode vocabulary exists to prevent. So the distinction is carried by
   * **geometry and text**: a dotted square against amber's solid and paper's dashed, in neutral
   * ink, with the words spelled out. See `AppShell` for the full argument.
   */
  marker?: string;
  /** The phone's compression, on the same rule as `MODE_SHORT`: keep the consequence. */
  markerShort?: string;
  markerTitle?: string;
  /** Which mode every timestamp on screen is in. `UI-Design-Spec.md` line 120. */
  timestampMode: TimestampMode;
  /**
   * Switch it. **Optional, and its absence is what makes a preview honest** — omit it and the
   * mode renders as a label rather than as a control that does nothing.
   */
  onTimestampMode?: () => void;
  theme: 'dark' | 'light';
  onTheme: () => void;
  onKill: () => void;
}): JSX.Element {
  return (
    <header className="jmt-top">
      <div className="jmt-top__lead">
        {/* 🔴 **The phone's menu toggle, and it leads the bar.** Hidden above 720px, where the
            sidebar is always present and a control to reveal it would be a control that does
            nothing. `aria-expanded` is what makes it a toggle rather than two buttons. */}
        {onMenu ? (
          <button
            type="button"
            className="jmt-top__menu"
            onClick={onMenu}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close the menu' : 'Open the menu'}
            title={menuOpen ? 'Close the menu' : 'Open the menu'}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              {menuOpen ? (
                <path d="M18 6 6 18M6 6l12 12" />
              ) : (
                <path d="M3 6h18M3 12h18M3 18h18" />
              )}
            </svg>
          </button>
        ) : null}
        <h1 className="jmt-top__title">{title}</h1>
        {/* 🔴 **Two renderings of one fact, and only one is ever visible.** `PAPER · TESTNET` is
            ~140px, which on a 430px phone leaves a screen called *Research* nowhere to go. The
            compact form keeps what the pill is *for* — the answer to "is this real money" — and
            drops the half that repeats it.

            ⚠ **The live state compresses to `REAL MONEY`, not to `LIVE`.** Shortening by taking
            the first word would drop the consequence and keep the label, which is exactly the
            wrong half: `LIVE` is a mode name, `REAL MONEY` is what it means. The colour law and
            the mode edge are unchanged either way. */}
        {marker ? (
          <>
            {/* ⚠ **The pill is a child of `.jmt-top__mode`, not that class plus another.** Putting
                `jmt-stk-broker` directly on `.jmt-top__modeshort` set `display: inline-flex` at
                the same specificity as the base `display: none` and later in the file, so both
                renderings showed at once — measured at 1600, where the header read
                `BROKER · READ-ONLY  BROKER`. The phone form gets its own class for the same
                reason: `.jmt-top__modeshort` is styled `--paper` below 720px, and this marker is
                deliberately neither mode's colour. */}
            <span className="jmt-top__mode">
              <span className="jmt-stk-broker" title={markerTitle}>
                <span aria-hidden="true" className="jmt-stk-broker__mark" />
                {marker}
              </span>
            </span>
            <span className="jmt-stk-top-marker jmt-stk-broker" title={markerTitle}>
              <span aria-hidden="true" className="jmt-stk-broker__mark" />
              {markerShort ?? marker}
            </span>
          </>
        ) : (
          <>
            <span className="jmt-top__mode">
              <ModePill mode={mode} />
            </span>
            {/* ⚠ Guarded on `mode`, not rendered unconditionally. With a null mode the label
                resolves to an empty string and this used to leave a bare coloured dot in the
                header — a mode indicator saying nothing, which is worse than none. */}
            {mode ? (
              <span
                className="jmt-top__modeshort"
                data-mode={mode}
                title={MODE_FULL[mode] ?? undefined}
              >
                <span aria-hidden="true" className="jmt-top__modedot" />
                {MODE_SHORT[mode] ?? ''}
              </span>
            ) : null}
          </>
        )}
      </div>

      {/* The flexible middle. Everything to its right is anchored to the trailing edge, which
          is what makes the kill switch's position independent of the pill's width. */}
      <span className="jmt-top__gap" />

      <div className="jmt-top__tail">

        {/* 🔴 **The mode is always visible and now it is also the switch** —
            `UI-Design-Spec.md` line 120: *"UTC by default with a toggle for local time, and the
            active mode always visible in the header."* The indicator shipped; the toggle did not,
            and `AppShell` passed a hardcoded string.

            ⚠ **The word carries the mode, never a colour.** The accent is not a state or a mode,
            so there is no active tint here — an operator reads `UTC` or `Local`, which is also
            what makes it legible in a screenshot.

            The previous copy justified the hardcoding as *"Every time in this interface is UTC
            (H5)"*. H5 is `ensure_utc` refusing a naive datetime — it governs what the system
            **stores**, and this governs what a human is shown. Nothing here changes a stored
            value. */}
        {onTimestampMode ? (
          <button
            type="button"
            className="jmt-top__tz jmt-top__tz--button"
            onClick={onTimestampMode}
            aria-pressed={timestampMode === 'local'}
            aria-label={`Timestamps are ${MODE_LABELS[timestampMode]}. Switch to ${
              timestampMode === 'utc' ? MODE_LABELS.local : MODE_LABELS.utc
            }.`}
            title={
              timestampMode === 'utc'
                ? `Every time on screen is UTC. Switch to local time${zoneSuffix()}.`
                : `Every time on screen is local time${zoneSuffix()}. Switch to UTC.`
            }
          >
            {MODE_LABELS[timestampMode]}
          </button>
        ) : (
          // No handler, so no control. A preview showing a toggle that does nothing would be a
          // gallery lying about the product.
          <span className="jmt-top__tz">{MODE_LABELS[timestampMode]}</span>
        )}

        {/* ⚠ **A 28px icon button, and the size is the point.** This was a bare unlabelled moon
            glyph, then a full-width labelled switch row at the foot of the sidebar, and the
            operator asked for it smaller and back up here. That is the correct weight: theme is
            set once and never again, and a labelled row with its own switch gave a once-a-year
            preference the same footprint as a screen in the nav.

            The label problem the sidebar row was solving is solved by `aria-label` plus a
            `title` instead. A control needs a *name*; it does not need the name printed beside
            it at all times. */}
        <button
          type="button"
          className="jmt-top__icon"
          onClick={onTheme}
          aria-pressed={theme === 'light'}
          aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
          title={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
        >
          <Icon name={theme === 'dark' ? 'moon' : 'sun'} size={16} />
        </button>

        <KillSwitch onClick={onKill} />
      </div>
    </header>
  );
}
